import React from 'react';
import { ArrowRight, Clock3, LayoutGrid, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ToolGrid } from '@/widgets/ToolGrid';
import { TOOLS, TOOL_CATEGORIES } from '@/shared/config/tools';
import landingPages from '@/shared/config/landing-pages.json';
import { getFrequentToolIds } from '@/shared/lib/tool-usage';
import { buttonVariants } from '@/shared/ui/Button';

const POPULAR_TOOL_IDS = [
  'json-beautifier',
  'html-preview',
  'format-converter',
  'base64',
  'image-converter',
  'api-request',
];

export function HomePage() {
  const [frequentToolIds, setFrequentToolIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    setFrequentToolIds(getFrequentToolIds());
  }, []);

  const frequentTools = frequentToolIds
    .map((id) => TOOLS.find((tool) => tool.id === id))
    .filter((tool): tool is (typeof TOOLS)[number] => Boolean(tool));
  const popularTools = POPULAR_TOOL_IDS.map((id) => TOOLS.find((tool) => tool.id === id)).filter(
    (tool): tool is (typeof TOOLS)[number] => Boolean(tool),
  );
  const quickAccessTools = [
    ...frequentTools,
    ...popularTools.filter((tool) => !frequentToolIds.includes(tool.id)),
  ].slice(0, 6);

  return (
    <div className="space-y-10 pb-12">
      <div className="space-y-4 py-6 text-center sm:py-8">
        <h1 className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
          Free Online Developer &amp; Conversion Tools
        </h1>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          Convert data, encode values, format code, and prepare files with fast browser-based tools
          from EasyUtils.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <Link to="/all-tools" className={buttonVariants()}>
            <LayoutGrid className="mr-2 h-4 w-4" />
            Browse all tools
          </Link>
          <p className="flex items-center gap-2 px-3 text-sm text-muted-foreground">
            <Search className="h-4 w-4" />
            <span className="sm:hidden">Tap search to find a tool by task</span>
            <span className="hidden items-center gap-2 sm:inline-flex">
              Press{' '}
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                ⌘/Ctrl K
              </kbd>{' '}
              to search by task
            </span>
          </p>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4 border-b pb-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <Clock3 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Quick access</h2>
              <p className="text-sm text-muted-foreground">
                {frequentTools.length > 0 ? 'Your most-used tools' : 'Popular tools to get started'}
              </p>
            </div>
          </div>
          <Link
            to="/all-tools"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            All tools
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <ToolGrid tools={quickAccessTools} />
      </section>

      <section className="grid gap-6 border-t pt-10 md:grid-cols-2">
        <div className="space-y-3">
          <h2 className="text-2xl font-semibold tracking-tight">Popular conversion guides</h2>
          <p className="leading-7 text-muted-foreground">
            Open a focused converter with an example input, expected output, and practical usage
            notes.
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {landingPages.map((page) => (
              <li key={page.path}>
                <Link className="font-medium text-primary hover:underline" to={page.path}>
                  {page.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-3 rounded-xl border bg-card p-6">
          <h2 className="text-2xl font-semibold tracking-tight">Clear, direct, browser-first</h2>
          <p className="leading-7 text-muted-foreground">
            EasyUtils gives every tool a stable public URL, concise instructions, and copy-ready
            output. Most tools run in your browser; network tools clearly identify when a request
            must leave the page.
          </p>
          <div className="flex gap-4 text-sm font-medium">
            <Link className="text-primary hover:underline" to="/about">
              About EasyUtils
            </Link>
            <Link className="text-primary hover:underline" to="/privacy">
              Privacy details
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function AllToolsPage() {
  return (
    <div className="space-y-12 pb-12">
      <div className="space-y-3 py-6 text-center sm:py-8">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">All developer tools</h1>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          Browse every available tool by category, or use search to jump directly to a task.
        </p>
      </div>

      <nav
        aria-label="Jump to a tool category"
        className="sticky top-16 z-20 border-y bg-background/95 px-4 py-3 backdrop-blur"
      >
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-2">
          {TOOL_CATEGORIES.map((category) => (
            <a
              key={category.id}
              href={`#${category.id}`}
              className="rounded-full border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition-all hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {category.name}
            </a>
          ))}
        </div>
      </nav>

      {TOOL_CATEGORIES.map((category) => (
        <section id={category.id} key={category.id} className="scroll-mt-32 space-y-4">
          <div className="flex items-center justify-between gap-4 border-b pb-2">
            <h2 className="text-xl font-semibold tracking-tight">{category.name}</h2>
            <span className="text-sm text-muted-foreground">
              {TOOLS.filter((tool) => tool.category === category.id).length} tools
            </span>
          </div>
          <ToolGrid category={category.id} />
        </section>
      ))}
    </div>
  );
}
